import { createSelector } from 'reselect';
import { selectSliceCisview } from '../../selectors/HELPERS';
import { ROOT_SLICE } from './constants';

import { EMPTY_ARRAY } from '../../../components/utils/sync';

const EMPTY_OBJECT = [];

const isCisviewState = state => {
	const keyMap = Object.keys(state).reduce((acc, k) => {
		acc[k] = true;
		return acc;
	}, {});

	const checkForKeys = [ROOT_SLICE];
	return checkForKeys.every(k => keyMap[k]);
};

export const selectRootSlice = state => {
	const sliceState = selectSliceCisview(state);
	if (isCisviewState(sliceState)) {
		return sliceState[ROOT_SLICE];
	}

	// assume local slice state;
	return sliceState;
};

// **********
// CORE
// **********

export const selectData = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { data } = slice;
	return data;
};

export const selectFilteredPmctStore = createSelector(selectData, (pmctStore) => {
	const result = {};
	const customers = Object.keys(pmctStore);
	customers.forEach(customer => {
		const { cts, pms } = pmctStore[customer];
		result[customer] = {};
		result[customer].pms = (pms || []).filter(u => u.fullName || u.fauxUserid || u.userid)
		result[customer].cts = (cts || []).filter(u => u.fullName || u.fauxUserid || u.userid)
	});
	return result;
})

export const selectPmctStoreMaps = createSelector(selectFilteredPmctStore, (pmctStore) => {
	const result = {};
	const _initMapForCustomer = () => ({
		pms: {
			userid: {},
			name: {},
		},
		cts: {
			userid: {},
			name: {},
		},
	});
	const customers = Object.keys(pmctStore);
	customers.forEach(customer => {
		const { cts, pms } = pmctStore[customer];
		result[customer] = result[customer] || _initMapForCustomer();
		cts.forEach(ct => {
			if (ct.userid) {
				result[customer].cts.userid[ct.userid] = ct;
			}
			if (ct.fullName) {
				result[customer].cts.name[ct.fullName] = ct;
			}
			if (!ct.fullName && ct.fauxUserid && !result[customer].cts.name[ct.fauxUserid]) {
				result[customer].cts.name[ct.fauxUserid] = ct;
			}
		})
		pms.forEach(pm => {
			if (pm.userid) {
				result[customer].pms.userid[pm.userid] = pm;
			}
			if (pm.fullName) {
				result[customer].pms.name[pm.fullName] = pm;
			}
			if (!pm.fullName && pm.fauxUserid && !result[customer].pms.name[pm.fauxUserid]) {
				result[customer].pms.name[pm.fauxUserid] = pm;
			}
		})
		
	});
	return result;
});

export const selectDataByCustomer = (stateAdmin, { customer }) => {
	const data = selectData(stateAdmin);
	return data[customer] || EMPTY_ARRAY;
};

export const selectIsLoading = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { isLoading } = slice;
	return isLoading;
};

export const selectIsLoaded = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { isLoaded } = slice;
	return isLoaded;
};

export const selectErrorMessage = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { errorMessage } = slice;
	return errorMessage;
};

// **********
// MAPS
// **********

export const selectUseridMaps = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { useridMaps } = slice;
	return useridMaps;
};

export const selectUseridMapByCustomer = (stateAdmin, { customer }) => {
	const useridMaps = selectUseridMaps(stateAdmin);
	return useridMaps[customer] || EMPTY_OBJECT;
};

export const selectUserFullNameMaps = stateAdmin => {
	const slice = selectRootSlice(stateAdmin);
	const { userFullNameMaps } = slice;
	return userFullNameMaps;
};

export const selectUserFullNameMapByCustomer = (stateAdmin, { customer }) => {
	const userFullNameMaps = selectUseridMaps(stateAdmin);
	return userFullNameMaps[customer] || EMPTY_OBJECT;
};

// **********
// VALIDATION
// **********

export const selectIsValidPm = (stateAdmin, { customer, userid, fullName }) => {
	const useridMap = selectUseridMapByCustomer(stateAdmin, { customer });
	const userFullNameMap = selectUserFullNameMapByCustomer(stateAdmin, {
		customer
	});
	return (
		(useridMap?.pms || {})[userid] ||
		(userFullNameMap?.pms || {})[fullName] ||
		false
	);
};

export const selectIsValidCt = (stateAdmin, { customer, userid, fullName }) => {
	const useridMap = selectUseridMapByCustomer(stateAdmin, { customer });
	const userFullNameMap = selectUserFullNameMapByCustomer(stateAdmin, {
		customer
	});
	return (
		(useridMap?.pms || {})[userid] ||
		(userFullNameMap?.pms || {})[fullName] ||
		false
	);
};

export default {
	selectData,
	selectDataByCustomer,
	selectIsLoading,
	selectIsLoaded,
	selectErrorMessage,

	selectUseridMaps,
	selectUseridMapByCustomer,
	selectUserFullNameMaps,
	selectUserFullNameMapByCustomer,

	selectIsValidPm,
	selectIsValidCt
};
